/**
 * Edit Study Dialog — edit study metadata fields.
 *
 * Uses the full updateStudy JSON-RPC endpoint (Sprint 3) to update study
 * fields including description, modality, body part, accession number,
 * physician, reason, history, priority, and comment.
 * Includes a "Generate Acc #" button that calls generateAccNumber.
 */
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Grid, IconButton, Tooltip,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { api } from '../../api/rpc';
import type { Study } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';

interface EditStudyDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete?: () => void;
}

export const EditStudyDialog = ({ open, study, onClose, onComplete }: EditStudyDialogProps) => {
  const [description, setDescription] = useState('');
  const [modality, setModality] = useState('');
  const [bodyPart, setBodyPart] = useState('');
  const [accNum, setAccNum] = useState('');
  const [physician, setPhysician] = useState('');
  const [reason, setReason] = useState('');
  const [history, setHistory] = useState('');
  const [priority, setPriority] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open && study) {
      setDescription(study.descr || '');
      setModality(study.mod || '');
      setBodyPart(study.bod || '');
      setAccNum(study.acc || '');
      setPhysician(study.phy || study.physician || '');
      setReason(study.reason || '');
      setHistory(study.history || '');
      setPriority(study.details?.priority as string || '');
      setComment(study.comment || '');
    }
  }, [open, study]);

  const handleGenerateAcc = async () => {
    if (!study) return;
    setGenerating(true);
    try {
      const updated = await api.generateAccNumber(study.objKey);
      if (updated.acc) setAccNum(updated.acc);
      pushSuccess('Accession number generated');
    } catch (e) {
      pushError('Failed to generate accession #: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!study) return;
    if (!accNum.trim()) {
      pushError('Accession number is required');
      return;
    }
    setSaving(true);
    try {
      await api.updateStudy({
        objKey: study.objKey,
        patObjKey: study.patientKey || '',
        acc: accNum,
        descr: description || undefined,
        mod: modality || undefined,
        bod: bodyPart || undefined,
        phy: physician || undefined,
        priority: priority || undefined,
        reason: reason || undefined,
        history: history || undefined,
        comment: comment || undefined,
      });
      pushSuccess('Study updated');
      onComplete?.();
      onClose();
    } catch (e) {
      pushError('Failed to update study: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
        Edit Study{study ? ` — ${study.niceName || study.name}` : ''}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth size="small" label="Description"
                value={description} onChange={(e) => setDescription(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
              />
            </Grid>
            <Grid size={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  fullWidth size="small" label="Accession #"
                  value={accNum} onChange={(e) => setAccNum(e.target.value)}
                  sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
                />
                <Tooltip title="Generate accession number">
                  <IconButton size="small" onClick={handleGenerateAcc} disabled={generating}>
                    <AutorenewIcon fontSize="small" sx={{ animation: generating ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid size={3}>
              <TextField
                fullWidth size="small" label="Modality"
                value={modality} onChange={(e) => setModality(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                fullWidth size="small" label="Body Part"
                value={bodyPart} onChange={(e) => setBodyPart(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" label="Physician"
                value={physician} onChange={(e) => setPhysician(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" label="Priority"
                value={priority} onChange={(e) => setPriority(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" label="Reason for Exam"
                value={reason} onChange={(e) => setReason(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" label="History" multiline rows={2}
                value={history} onChange={(e) => setHistory(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" label="Comment"
                value={comment} onChange={(e) => setComment(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>
          Cancel
        </Button>
        <Button
          size="small" variant="contained" disabled={saving}
          onClick={handleSave}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
